import type { MedplumClient } from '@medplum/core';
import type { Appointment, Identifier } from '@medplum/fhirtypes';
import {
  IN_PERSON_RECORDING_ENDED_AT_EXT,
  IN_PERSON_RECORDING_STARTED_AT_EXT,
} from '@unify/fhir-model/constants';
import {
  isZoomTranscriptAbsent,
  isZoomTranscriptReady,
} from '@/lib/aiNoteGenerationService';

const IN_PERSON_RECORDING_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'zoom-in-person-recording',
} as const;

export type InPersonRecordingStartResult = {
  meetingNumber: string;
  password: string;
  meetingId: string;
};

function getBotExecuteTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_IN_PERSON_RECORDING_BOT_ID?.trim();
  if (id) return id;
  return IN_PERSON_RECORDING_BOT_IDENTIFIER;
}

export async function startInPersonRecording(
  medplum: MedplumClient,
  appointmentId: string,
): Promise<InPersonRecordingStartResult> {
  return medplum.executeBot(getBotExecuteTarget(), {
    action: 'start',
    appointmentId,
  }) as Promise<InPersonRecordingStartResult>;
}

/** Stamp started-at after the host Meeting SDK join succeeds. */
export async function markInPersonRecordingJoined(
  medplum: MedplumClient,
  appointmentId: string,
): Promise<{ joined: boolean }> {
  return medplum.executeBot(getBotExecuteTarget(), {
    action: 'joined',
    appointmentId,
  }) as Promise<{ joined: boolean }>;
}

export async function endInPersonRecording(
  medplum: MedplumClient,
  appointmentId: string,
  options?: { expectedMeetingId?: string },
): Promise<{ ended: boolean; skipped?: boolean }> {
  return medplum.executeBot(getBotExecuteTarget(), {
    action: 'end',
    appointmentId,
    ...(options?.expectedMeetingId
      ? { expectedMeetingId: options.expectedMeetingId }
      : {}),
  }) as Promise<{ ended: boolean; skipped?: boolean }>;
}

/**
 * Recording page URL. When meeting credentials from start are available, pass them as
 * query params (same pattern as telehealth ZoomCallPage) so join does not depend solely
 * on a fresh FHIR Appointment read after hard navigation.
 */
export function inPersonRecordingPath(
  appointmentId: string,
  meeting?: Pick<InPersonRecordingStartResult, 'meetingNumber' | 'password'> & {
    sessionId?: string;
  },
): string {
  const base = `/dashboard/appointments/${encodeURIComponent(appointmentId)}/in-person-recording`;
  const meetingNumber = meeting?.meetingNumber?.trim();
  if (!meetingNumber) return base;
  const params = new URLSearchParams({
    zoomMeetingNumber: meetingNumber,
    zoomPassword: meeting?.password?.trim() ?? '',
  });
  if (meeting?.sessionId?.trim()) {
    params.set('sessionId', meeting.sessionId.trim());
  }
  return `${base}?${params.toString()}`;
}

/**
 * True when a recording session (or transcript) already exists — not merely a
 * pre-created Zoom meeting on the appointment.
 */
export function hasPriorInPersonRecording(appointment: Appointment | undefined): boolean {
  if (!appointment) return false;
  const startedOrEnded = appointment.extension?.some(
    (e) =>
      (e.url === IN_PERSON_RECORDING_STARTED_AT_EXT ||
        e.url === IN_PERSON_RECORDING_ENDED_AT_EXT) &&
      Boolean(e.valueDateTime?.trim()),
  );
  return (
    Boolean(startedOrEnded) ||
    isZoomTranscriptReady(appointment) ||
    isZoomTranscriptAbsent(appointment)
  );
}

export function isInPersonRecordingSessionActive(appointment: Appointment | undefined): boolean {
  if (!appointment?.extension?.length) return false;
  const started = appointment.extension.find(
    (e) => e.url === IN_PERSON_RECORDING_STARTED_AT_EXT,
  )?.valueDateTime;
  const ended = appointment.extension.find(
    (e) => e.url === IN_PERSON_RECORDING_ENDED_AT_EXT,
  )?.valueDateTime;
  return Boolean(started) && !ended;
}
